/**
 * Canonical phone storage: Country_Code (+91) + local digits only.
 * Join only for display / tel / WhatsApp.
 */

export const DEFAULT_COUNTRY_CODE = "+91";

export function digitsOnly(value) {
  return String(value ?? "").replace(/\D/g, "");
}

export function normalizeCountryCode(code, fallback = DEFAULT_COUNTRY_CODE) {
  let c = String(code ?? "").trim();
  if (!c) return fallback;
  if (!c.startsWith("+")) c = `+${c.replace(/^0+/, "")}`;
  const digits = digitsOnly(c);
  // Dial codes are 1–3 digits; allow up to "+xxx" (4 chars). Reject junk only.
  if (digits.length < 1 || digits.length > 3) return fallback;
  return `+${digits}` || fallback;
}

/**
 * Split a raw phone (possibly "+91 98765..." or "919876543210") into
 * { countryCode, number } where number is local digits only.
 */
export function splitPhone(raw, fallbackCountryCode = DEFAULT_COUNTRY_CODE) {
  const s = String(raw ?? "").trim();
  if (!s) {
    return { countryCode: normalizeCountryCode(fallbackCountryCode), number: "" };
  }

  const isValidCc = (cc) => {
    const digits = digitsOnly(cc);
    return digits.length >= 1 && digits.length <= 3 && String(cc).length <= 5;
  };

  if (s.startsWith("+")) {
    // Prefer explicit separator after the dial code: "+91 98765..." / "+91-98765..."
    const m = s.match(/^(\+\d{1,3})[\s\-./]+(.*)$/);
    if (m) {
      const rest = digitsOnly(m[2]);
      if (rest && isValidCc(m[1])) {
        return { countryCode: m[1], number: rest };
      }
    }

    const all = digitsOnly(s);
    if (all.length >= 12 && all.startsWith("91")) {
      let local = all.slice(2);
      if (local.length > 10) local = local.slice(-10);
      return { countryCode: "+91", number: local };
    }
    if (all.length > 10) {
      const cc = `+${all.slice(0, -10)}`;
      if (isValidCc(cc)) {
        return { countryCode: cc, number: all.slice(-10) };
      }
      return {
        countryCode: normalizeCountryCode(fallbackCountryCode),
        number: all.slice(-10),
      };
    }
    if (all) {
      return {
        countryCode: normalizeCountryCode(fallbackCountryCode),
        number: all,
      };
    }
    return { countryCode: normalizeCountryCode(fallbackCountryCode), number: "" };
  }

  const all = digitsOnly(s);
  if (!all) {
    return { countryCode: normalizeCountryCode(fallbackCountryCode), number: "" };
  }

  // Common India / prefixed full numbers without plus
  if (all.length > 10 && all.startsWith("91") && all.length <= 13) {
    return { countryCode: "+91", number: all.slice(2) };
  }
  if (all.length > 10) {
    const cc = `+${all.slice(0, -10)}`;
    if (isValidCc(cc)) {
      return { countryCode: cc, number: all.slice(-10) };
    }
    return {
      countryCode: normalizeCountryCode(fallbackCountryCode),
      number: all.slice(-10),
    };
  }

  return {
    countryCode: normalizeCountryCode(fallbackCountryCode),
    number: all,
  };
}

/** Local digits only for DB storage (null if empty). */
export function toLocalPhone(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const { number } = splitPhone(raw);
  return number || null;
}

/** Join for dialing / WhatsApp (no spaces): +919876543210 */
export function joinPhoneE164(countryCode, number) {
  const local = digitsOnly(number);
  if (!local) return "";
  const cc = normalizeCountryCode(countryCode).replace(/\D/g, "");
  return `+${cc}${local}`;
}

/** Local part only, 3-3-4: 987-654-3210 (use formatPhoneDisplay to show a number). */
export function formatLocalPhoneDashed(number) {
  const local = digitsOnly(number);
  if (!local) return "";
  if (local.length > 6) {
    return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
  }
  if (local.length > 3) {
    return `${local.slice(0, 3)}-${local.slice(3)}`;
  }
  return local;
}

/**
 * Standard phone display everywhere: country code + 3-3-4 groups, e.g. "+91 991-259-8303".
 * The country code is always shown (+91 when none is stored); a number that already carries one
 * ("+1 415...", "919912598303") keeps it.
 */
export function formatPhoneDisplay(countryCode, number) {
  const raw = String(number ?? "").trim();
  if (!digitsOnly(raw)) return "";
  const hasOwnCode = raw.startsWith("+") || (!String(countryCode ?? "").trim() && digitsOnly(raw).length > 10);
  const { countryCode: cc, number: local } = hasOwnCode
    ? splitPhone(raw.startsWith("+") ? raw : `+${digitsOnly(raw)}`)
    : { countryCode: normalizeCountryCode(countryCode), number: digitsOnly(raw) };
  return `${cc} ${formatLocalPhoneDashed(local)}`;
}

/** Same as formatPhoneDisplay (kept for existing callers). */
export function formatPhoneSpaced(countryCode, number) {
  return formatPhoneDisplay(countryCode, number);
}

/** { display: "+91 987-654-3210", href: "tel:+919876543210" }, or null when there is no number. */
export function phoneCallLink(countryCode, number) {
  const raw = String(number ?? "").trim();
  if (!digitsOnly(raw)) return null;
  const { countryCode: cc, number: local } = raw.startsWith("+")
    ? splitPhone(raw)
    : { countryCode: normalizeCountryCode(countryCode), number: digitsOnly(raw) };
  return { display: formatPhoneSpaced(cc, local), href: `tel:${joinPhoneE164(cc, local)}` };
}

/** "+91 987-654-3210" (country code always shown; +91 when none is stored). */
export function joinPhoneDisplay(countryCode, number) {
  return formatPhoneDisplay(countryCode, number);
}

export const MOBILE_DIGIT_COUNT = 10;

/** Form fields that store a local 10-digit mobile number. */
export const MOBILE_FORM_FIELDS = new Set([
  "personalPhone",
  "phone",
  "phoneNumber",
  "altPhone",
  "altNumber",
  "mobileNumber",
  "contactNumber",
  "contactPhone",
  "supportPhone",
  "primaryContactPhone",
  "emergencyContactNumber",
  "alternatePhone",
  "companyPhone",
  "Mobile_Number",
  "Alt_Phone",
]);

export function sanitizeMobileInput(value) {
  return digitsOnly(value).slice(0, MOBILE_DIGIT_COUNT);
}

export function isValidMobileNumber(value, { required = false } = {}) {
  const digits = digitsOnly(value);
  if (!digits) return !required;
  return digits.length === MOBILE_DIGIT_COUNT;
}

export function mobileValidationMessage(value, { required = false, label = "Mobile number" } = {}) {
  const digits = digitsOnly(value);
  if (!digits) {
    return required ? `${label} is required.` : null;
  }
  if (digits.length !== MOBILE_DIGIT_COUNT) {
    return `${label} must be exactly 10 digits.`;
  }
  return null;
}

export function formValueWithMobileSanitize(name, value) {
  if (MOBILE_FORM_FIELDS.has(name)) return sanitizeMobileInput(value);
  return value;
}
